export function toFiniteNumber(value) {
  if (typeof value !== "number" && typeof value !== "string") return null;
  if (typeof value === "string" && !value.trim()) return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

const formatNumber = (value, decimals = 0) => {
  const number = toFiniteNumber(value);
  if (number === null) return "—";

  const absolute = Math.abs(number);
  const [integer, fraction] = absolute.toFixed(decimals).split(".");
  const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, "'");
  const sign = number < 0 ? "-" : "";

  return `${sign}${grouped}${fraction !== undefined ? `.${fraction}` : ""}`;
};

const formatCompactCurrency = (value, currency) => {
  const number = toFiniteNumber(value);
  if (number === null) return "—";

  const absolute = Math.abs(number);
  const sign = number < 0 ? "-" : "";

  if (absolute >= 1000000) {
    const millions = Number.parseFloat((absolute / 1000000).toFixed(2));
    return `${currency} ${sign}${millions}m`;
  }

  if (absolute >= 1000) {
    return `${currency} ${sign}${Math.round(absolute / 1000)}k`;
  }

  return `${currency} ${sign}${Math.round(absolute)}`;
};

export function formatKpiValue(value, unit, currency = "CHF") {
  const number = toFiniteNumber(value);
  if (number === null) return "—";

  if (unit === "percent") {
    return `${formatNumber(number * 100, 1)}%`;
  }

  if (unit === "currencyPerHour") {
    return `${currency} ${formatNumber(number, 2)}/h`;
  }

  return `${currency} ${formatNumber(number, 0)}`;
}

export function getKpiUnitLabel(unit, currency = "CHF") {
  if (unit === "percent") return "%";
  if (unit === "currencyPerHour") return `${currency}/h`;
  return currency;
}

export function formatKpiBadgeValue(value, unit) {
  const number = toFiniteNumber(value);
  if (number === null) return "—";
  if (unit === "percent") return formatNumber(number * 100, 1);
  if (unit === "currencyPerHour") return formatNumber(number, 2);
  return formatNumber(number, 0);
}

export function formatKpiBadgeDifference(value, unit) {
  const number = toFiniteNumber(value);
  if (number === null) return "—";

  const absolute = Math.abs(number);
  const sign = isKpiDifferenceRoundedToZero(number, unit) ? "" : number > 0 ? "+" : "-";

  if (unit === "percent") return `${sign}${formatNumber(absolute * 100, 1)}`;
  if (unit === "currencyPerHour") return `${sign}${formatNumber(absolute, 2)}`;
  return `${sign}${formatNumber(absolute, 0)}`;
}

export function isKpiDifferenceRoundedToZero(value, unit) {
  const number = toFiniteNumber(value);
  if (number === null) return true;
  if (unit === "percent") return Number(Math.abs(number * 100).toFixed(1)) === 0;
  if (unit === "currencyPerHour") return Number(Math.abs(number).toFixed(2)) === 0;
  return Number(Math.abs(number).toFixed(0)) === 0;
}

export function formatKpiScaleValue(value, unit, currency = "CHF") {
  const number = toFiniteNumber(value);
  if (number === null) return "—";

  if (unit === "percent") {
    return `${Math.round(number * 100)}%`;
  }

  if (unit === "currencyPerHour") {
    return `${currency} ${Math.round(number)}/h`;
  }

  return formatCompactCurrency(number, currency);
}

export function formatKpiDifference(value, unit, currency = "CHF") {
  const number = toFiniteNumber(value);
  if (number === null) return "—";

  const absolute = Math.abs(number);

  if (unit === "percent") {
    return `${formatNumber(absolute * 100, 1)}%`;
  }

  if (unit === "currencyPerHour") {
    return `${currency} ${formatNumber(absolute, 2)}/h`;
  }

  return `${currency} ${formatNumber(absolute, 0)}`;
}
